import { Template, Workflow } from "../interfaces";
import { getData } from '../backend';
import useSWRImmutable from 'swr/immutable';
import useUser from '../hooks/useUser';
import TemplateListCard from './TemplateListCard';
import heroIllustration from '../assets/hero_banner-illustration.svg';
import learnBg1 from '../assets/learn-bg-1.svg';
import learnBg2 from '../assets/learn-bg-2.svg';
import learnBg3 from '../assets/learn-bg-3.svg';
import { Link } from 'react-router-dom';
import HeroIcon from './HeroIcon';
import { useTranslation } from 'react-i18next';
import { useModal } from "../contexts";
import WorkflowSelectModal from "./WorkflowSelectModal";
import WorkflowListCard from "./WorkflowListCard";
import useWorkflows from "../hooks/useWorkflows";

interface Props {
  contentOffset: number,
  createNewWorkflow: (stage: "SOURCE" | "DOCUMENT", blockType: string, file?: any, isPdf?: boolean, options?: Record<string, any>) => void;
}

function Home(props: Props) {
  const { t } = useTranslation();
  const { user, hasPermission } = useUser();
  const { openModal } = useModal();

  const { createNewWorkflow } = props;
  const { data: templates } = useSWRImmutable<Array<Template>>("/templates/", getData);

  const { data, mutate: mutateWorkflows } = useWorkflows();
  const { results: filteredWorkflows } = data ?? { results: undefined, numPages: 1, total: 0 };

  function handleAddWorkflow() {
    if (!hasPermission(user?.activeTeam, "workflows.create"))
      return;

    openModal(<WorkflowSelectModal title={t("New Workflow")} subtitle={t("Select a source or template document to start building a workflow")} showDocuments showSources onSelection={createNewWorkflow} />);
  }

  if (user === undefined)
    return <img src="https://static.portant.co/portant-loading-gray.svg" className="w-24 h-24 m-auto" alt="Loading Logo" />;

  return (
    <div className="border border-gray-200 rounded-lg bg-white p-4 pb-20 w-full">

      {/* Hero Banner Section */}
      <div className="flex items-center justify-between rounded-md mb-8 px-[64px] py-[24px]" style={{ background: "linear-gradient(180deg, #0E43DC 0%, #026AE7 33.17%, #0080E3 66.83%, #A1CDF1 100%), #F0F0F0" }}>

        {/* Left Content */}
        <div className="flex flex-col justify-center">
          <div className="mb-3">
            <h1 className="text-white font-gilroy font-semibold text-4xl">
              {t("Say goodbye to manual paperwork")}
            </h1>
          </div>
          <p className="text-white mb-8">
            {t("Integrate, customize, and automate your sales documents in seconds.")}
          </p>
          <div className="flex items-center gap-4">
            <div className="bg-gradient-to-r from-blue to-white p-px rounded-lg w-fit">
              <Link to="/templates" className="block bg-black text-white px-4 py-2 rounded-lg font-gilroy font-semibold text-sm hover:bg-gray-900 transition-colors w-fit">
                {t("Kickstart with templates")}
              </Link>
            </div>
            <button className="text-white font-gilroy text-sm hover:underline transition-colors" onClick={handleAddWorkflow}>
              {t("Build workflow from scratch")}
            </button>
          </div>
        </div>

        {/* Right Video Section */}
        <div className="rounded-md overflow-hidden relative flex-shrink-0 w-[500px] h-[285px]">
          <img
            src={heroIllustration}
            alt="Hero Banner Illustration"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Learn Rail Section */}
      <div className="mx-4 mb-8">
        <h2 className="text-black font-gilroy font-semibold mb-6 text-left text-base">
          {t("Learn Portant")}
        </h2>

        <div className="flex gap-7">
          {/* Learn Card 1 */}
          <div
            className="border border-gray-200 rounded-lg cursor-pointer learn-card-hover transition-all w-full h-[200px] relative overflow-hidden"
            onClick={() => {
              window.open('https://www.youtube.com/watch?v=w_fnAyCBBpg', '_blank');
            }}
          >
            {/* Background SVG behind content */}
            <img src={learnBg1} alt="" className="absolute inset-0 w-full h-full object-cover" />

            {/* Content overlay */}
            <div className="relative z-10 p-4 h-full flex flex-col">
              <div className="w-8 h-8 rounded mb-4 flex items-center justify-center">
                <HeroIcon icon="AcademicCapIcon" className="w-6 h-6 text-blue" />
              </div>
              <h3 className="text-black font-gilroy font-semibold mb-2 text-3xl leading-8">
                {t("01. Get Started")}
              </h3>
              <p className="text-gray-600 font-gilroy text-xs leading-4 max-w-64">
                {t("Portant beginners start here! Learn the basics fast with helpful onboarding tips.")}
              </p>
            </div>
          </div>

          {/* Learn Card 2 */}
          <div className="border border-gray-200 rounded-lg cursor-pointer learn-card-hover transition-all w-full h-[200px] relative overflow-hidden"
            onClick={() => {
              window.open('https://www.youtube.com/playlist?list=PLkgWiQcDfqwXNxzt5yGwHfsuBWqS9vK69', '_blank');
            }}
          >
            {/* Background SVG behind content */}
            <img src={learnBg2} alt="" className="absolute inset-0 w-full h-full object-cover" />

            {/* Content overlay */}
            <div className="relative z-10 p-4 h-full flex flex-col">
              <div className="w-8 h-8 rounded mb-4 flex items-center justify-center">
                <HeroIcon icon="LightningBoltIcon" className="w-6 h-6 text-blue" />
              </div>
              <h3 className="text-black font-gilroy font-semibold mb-2 text-3xl leading-8">
                {t("02. Workflows")}
              </h3>
              <p className="text-gray-600 font-gilroy text-xs leading-4 max-w-64">
                {t("Create powerful workflows to automate your sales document processes in minutes.")}
              </p>
            </div>
          </div>

          {/* Learn Card 3 */}
          <div
            className="border border-gray-200 rounded-lg cursor-pointer learn-card-hover transition-all w-full h-[200px] relative overflow-hidden"
            onClick={() => {
              window.open('https://www.youtube.com/watch?v=DH5EC7vHD9Q', '_blank');
            }}
          >
            {/* Background SVG behind content */}
            <img src={learnBg3} alt="" className="absolute inset-0 w-full h-full object-cover" />

            {/* Content overlay */}
            <div className="relative z-10 p-4 h-full flex flex-col">
              <div
                className="w-8 h-8 rounded mb-4 flex items-center justify-center">
                <HeroIcon icon="PencilAltIcon" className="w-6 h-6 text-blue" />
              </div>
              <h3 className="text-black font-gilroy font-semibold mb-2 text-3xl leading-8">
                {t("03. Signatures")}
              </h3>
              <p className="text-gray-600 font-gilroy text-xs leading-4 max-w-64">
                {t("Automate documents eSignatures to streamline contracts, agreements, and more.")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent workflow section */}
      {(filteredWorkflows || []).length >= 2 &&
        <div className="mx-4 mb-8">
          <Link to={"/workflows"} >
            <h2 className="text-black font-gilroy font-semibold mb-6 text-left text-base inline-block">
              <span className="hover:underline">{t("Recent Workflows")}</span>
            </h2>
          </Link>

          <div className="grid grid-cols-3 gap-7">
            {(filteredWorkflows || []).slice(0, 3).map((workflow: Workflow) => (
              <WorkflowListCard key={workflow.id} workflow={workflow} mutateWorkflowList={mutateWorkflows} />
            ))}
          </div>
        </div>
      }

      {/* Template Rail Section */}
      <div className="mx-4 mb-8">
        <h2 className="text-black font-gilroy font-semibold mb-6 text-left text-base">
          {t("Featured Templates")}
        </h2>

        <div className="grid grid-cols-3 gap-7">
          {(templates || []).slice(0, 6).map((template: Template) => (
            <TemplateListCard key={template.id} template={template} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default Home;
